// Eager slim slice of the canvas component barrel (#15).
//
// The full `./index.js` barrel `export *`s the heavy canvas editor
// (CanvasEditor, CanvasV2CodeEditor/Monaco, embed/*, CanvasOverlayV2, …) —
// ~800KB that the unified shell dragged into its EAGER chunk via
// `import * from '../canvas/components/index.js'` in workspace/context.js.
//
// Canvas is also a lazy sub-app: canvas/context.js re-registers the FULL
// barrels, but only on canvas-MANIFEST routes (/studio, /dashboard, /profile,
// /:owner/:project). The canvas-kind NON-manifest routes (/team-and-agents,
// /agents, /tickets, /files, /infra), `/`, and the shell-root chrome never load
// canvas/context.js — so every canvas component key they reference must live in
// THIS eager slice.
//
// canvas/package.json has no `sideEffects: false`, so a re-export through
// `./index.js` would NOT tree-shake (referencing it evaluates every sibling
// source file). Each key below is therefore `export *`-ed from its SOURCE file
// directly, so only these light modules ride the eager chunk.
//
//   PageSlot                                         → PageSlot.js
//   SettingsModalBody                                → SettingsModal.js
//   CanvasConnection                                 → CanvasConnection.js
//   MCPConnectorPickerBody, TransportModeBtn, AuthModeBtn → MCPConnectorPickerOverlay.js
//   CanvasPromptTextarea                             → CanvasPromptTextarea.js
//   NewProjectPickerBody, NpTemplateCard             → NewProjectPicker.js
//   ProjectCanvasSidebar, ProjectCanvasFilterRow, …  → ProjectCanvasSidebar.js
//   TeamPersonNode, TeamAgentNode, TeamMemberModal,
//   AgentModal, AgentPermChip, AgentToolRow,
//   AgentAssigneeRow, AgentAssignChip, teamAndAgents → ../pages/team-and-agents.js
export * from './PageSlot.js'
export * from './MinimizedDock.js'
export * from './SettingsModal.js'
export * from './CanvasConnection.js'
export * from './MCPConnectorPickerOverlay.js'
export * from './CanvasPromptTextarea.js'
export * from './NewProjectPicker.js'
// The sidebar hide/show pair is referenced by BARE KEY from the studio pages
// (studios.js, dashboard.js), which are reachable without canvas/context.js
// having registered the full barrel — so without this line the keys resolved
// to nothing and DOMQL rendered them as empty, zero-height divs: present in
// the DOM, invisible, no error (verified live on /studio — the registry held
// 1283 components and none of these three). Safe for the eager chunk: this
// module is self-contained with zero imports of its own.
export * from './ProjectCanvasSidebar.js'
// Entity (data-item) nodes. Both are reached by BARE KEY and never by import:
// `CvEntityCard` from CanvasItem's child tree, `CvEntityPickerBody` from the
// create island's `openCustomModal({ content: { extends: … } })`. Modal content
// is mounted by the shared ModalRoot, which lives on the SHELL root — outside
// canvas/context.js's registration — so without this line the picker would open
// as an empty panel with no error, the exact failure mode ProjectCanvasSidebar
// above is here to prevent. Both modules are self-contained (zero imports).
export * from './CvEntityCard.js'
export * from './CvEntityPickerBody.js'
// /team-and-agents (TeamPersonNode/TeamAgentNode/TeamMemberModal/AgentModal +
// its AgentModal row children AgentPermChip/AgentToolRow/AgentAssigneeRow/
// AgentAssignChip, plus the `teamAndAgents` page itself) is bare-keyed /
// string-extended off canvas-manifest routes: the home AgentsWidget embeds
// the page (`extends: 'teamAndAgents'`), and its node/modal components are
// referenced the same way. Previously the shell's own components/index.js
// carried these as a hand-written bridge re-export (WS-SHELL-CANVAS-
// EAGERSLICE-MERGE-1) because canvas was declared in the shell's
// symbols.json but never actually merged — this line is that merge's real
// source instead.
export * from '../pages/team-and-agents.js'
// The `canvas-projects` and `project-status` AI-answer views (functions/
// canvasAnswer.js, canvasStatus.js): the assistant's thread and a Fly page
// mount them on ANY route, never folding the canvas manifest — a grid of the
// shared ProjectCard and a status card, zero imports of their own.
export * from './CanvasProjectsView.js'
export * from './ProjectStatusView.js'
